/**
 * NewCustomerButton
 *
 * Botão "Novo Cliente" com escolha da origem do cadastro:
 *   - Manual     → abre o formulário tradicional (CustomerFormDialog)
 *   - Automático → abre o wizard de consulta em sistema externo (ERP)
 *
 * A opção "Automático" só aparece quando o tenant possui alguma conexão ativa
 * cujo provider declara a capacidade `customer.lookup_document` (multi-provider).
 * Sem conexões elegíveis, o botão dispara o cadastro manual direto (sem popover),
 * preservando o comportamento atual.
 *
 * RELEASE: 2026-08-23 v1.0.0 - Versão inicial (integração SGCNET).
 */

import { useState } from 'react';
import { Plus, PenLine, Zap, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDocumentLookupConnections } from '@/hooks/integrations/useCustomerDocumentLookup';

interface NewCustomerButtonProps {
  /** Abre o formulário manual de cliente */
  onManual: () => void;
  /** Abre o wizard de cadastro automático */
  onAutomatic: () => void;
  /** Oculta o rótulo em telas pequenas (padrão do header) */
  className?: string;
}

export function NewCustomerButton({ onManual, onAutomatic, className }: NewCustomerButtonProps) {
  const [open, setOpen] = useState(false);
  const { isAvailable } = useDocumentLookupConnections(true);

  // Sem integração elegível: mantém o botão simples de cadastro manual
  if (!isAvailable) {
    return (
      <Button size="xs" onClick={onManual} className={className}>
        <Plus className="sm:mr-1.5" />
        <span className="hidden sm:inline">Novo Cliente</span>
      </Button>
    );
  }

  /** Executa a ação escolhida e fecha o popover */
  const handleChoice = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="xs" className={className}>
          <Plus className="sm:mr-1.5" />
          <span className="hidden sm:inline">Novo Cliente</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-1.5">
        <p className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground">
          Como deseja cadastrar?
        </p>

        <button
          type="button"
          onClick={() => handleChoice(onManual)}
          className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted"
        >
          <PenLine className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">Manual</span>
            <span className="block text-[11px] text-muted-foreground">
              Preencher o formulário
            </span>
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </button>

        <button
          type="button"
          onClick={() => handleChoice(onAutomatic)}
          className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted"
        >
          <Zap className="h-4 w-4 text-primary shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">Automático</span>
            <span className="block text-[11px] text-muted-foreground">
              Buscar por CPF/CNPJ na integração
            </span>
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </button>
      </PopoverContent>
    </Popover>
  );
}
